import { useEffect, useState, useMemo } from "react";
import { api, ROLE_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import CommandStrategic from "@/pages/CommandStrategic";
import CommandExecutiveBrief from "@/pages/CommandExecutiveBrief";
import {
  Radio, AlertTriangle, Lock, Clock, CheckCircle2, Users, DollarSign, Activity,
  Plus, Edit3, Trash2, ChevronRight, X, MessageSquare, Link2, GitBranch, Zap,
} from "lucide-react";

const KIND_LABEL = {
  capital_project: "Capital Project",
  program_initiative: "Program Initiative",
  bcr_action: "BCR Action",
  funding_deliverable: "Funding Deliverable",
  hr_action: "HR Action",
  compliance_task: "Compliance",
  stakeholder_engagement: "Stakeholder Engagement",
  event: "Event",
};
const DEPT_LABEL = {
  GOV:"Governance", HTH:"Health", EDU:"Education", HSG:"Housing",
  CAP:"Capital Projects", LND:"Lands", SOC:"Social Services", ECO:"Economic Dev.", CUL:"Culture",
};
const PRIORITY_COLOR = { critical:"#ef5b6b", high:"#d4b876", medium:"#89b4fa", low:"#8C8C8C" };
const STATUS_COLOR = {
  not_started:"#8C8C8C", in_progress:"#89b4fa", on_track:"#89b4fa",
  at_risk:"#d4b876", blocked:"#ef5b6b", completed:"#89b4fa", cancelled:"#8C8C8C",
};
const HEALTH_ICON = { at_risk: AlertTriangle, blocked: Lock, completed: CheckCircle2 };

const currency = (n) => n == null ? "—" : "$" + Math.round(n).toLocaleString();

export default function Command() {
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [dockets, setDockets] = useState([]);
  const [staff, setStaff] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | docketObj
  const [viewing, setViewing] = useState(null);
  const [filter, setFilter] = useState({ priority: "all", department: "all", kind: "all" });
  const confirm = useConfirm();

  const loadAll = async () => {
    try {
      const [dashRes, docketsRes] = await Promise.all([
        api.get("/command/dashboard"),
        api.get("/command/dockets"),
      ]);
      setDash(dashRes.data);
      setDockets(docketsRes.data);
    } catch { /* interceptor handles auth */ }
  };
  const loadStaff = () => api.get("/command/staff").then(r=>setStaff(r.data)).catch(()=>{});

  useEffect(() => { loadAll(); loadStaff(); const t = setInterval(loadAll, 15000); return () => clearInterval(t); }, []);

  const filtered = useMemo(() => dockets.filter(d => {
    if (filter.priority !== "all" && d.priority !== filter.priority) return false;
    if (filter.department !== "all" && d.department !== filter.department) return false;
    if (filter.kind !== "all" && d.kind !== filter.kind) return false;
    return true;
  }), [dockets, filter]);

  const del = async (id) => {
    const ok = await confirm({ title: "Close docket?", destructive: true, message: "Removes the docket and unlinks any dependency references.", confirmLabel: "Delete" });
    if (!ok) return;
    try { await api.delete(`/command/dockets/${id}`); toast.success("Docket removed"); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!dash) return <div className="max-w-[1600px] mx-auto px-6 py-16 font-mono text-white/50">Establishing command channel…</div>;

  return (
    <div className="max-w-[1600px] mx-auto px-6 py-10">
      {/* Header */}
      <div className="flex justify-between items-baseline flex-wrap gap-3">
        <div>
          <div className="label-mono flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 bg-[color:var(--cobalt)] animate-pulse"/>
            Executive command center
          </div>
          <h1 className="font-display text-4xl mt-2 leading-tight">Operations</h1>
          <div className="font-mono text-[10px] text-white/40 mt-2 uppercase tracking-widest">
            {ROLE_LABEL[user?.role]}{user?.is_owner && " · owner"}{user?.is_super_admin && !user?.is_owner && " · super admin"} · Refresh 15s · {new Date(dash.generated_at).toLocaleTimeString()}
          </div>
        </div>
        <button onClick={()=>setEditing("new")} className="btn-cta primary" data-testid="new-docket-btn"><Plus size={12}/> Open docket</button>
      </div>

      {/* ============ EXECUTIVE BRIEF (cross-module cockpit) ============ */}
      <div className="mt-8">
        <CommandExecutiveBrief onNewDocket={() => setEditing("new")} />
      </div>

      {/* ============ STRATEGIC LAYER (board oversight) ============ */}
      <div className="mt-8">
        <CommandStrategic/>
      </div>

      {/* ============ OPERATIONAL LAYER (live ops) ============ */}
      <div className="flex items-center gap-3 mt-2 mb-4">
        <span className="inline-block w-1.5 h-1.5 bg-[#89b4fa] animate-pulse"/>
        <span className="label-mono text-[#89b4fa]">Live operations</span>
        <span className="font-mono text-[10px] text-white/40 hidden sm:inline">real-time state · 15s refresh</span>
      </div>

      {/* Vitals */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <VitalCard icon={Activity} color="#FFFFFF" label="Open Dockets" value={dash.vitals.total_open}/>
        <VitalCard icon={AlertTriangle} color="#ef5b6b" label="Critical" value={dash.vitals.critical}/>
        <VitalCard icon={Zap} color="#d4b876" label="At Risk" value={dash.vitals.at_risk}/>
        <VitalCard icon={Lock} color="#ef5b6b" label="Blocked" value={dash.vitals.blocked}/>
        <VitalCard icon={Clock} color="#d4b876" label="Overdue" value={dash.vitals.overdue}/>
        <VitalCard icon={CheckCircle2} color="#89b4fa" label="Done 7d" value={dash.vitals.completed_this_week}/>
      </div>

      {/* Filter bar */}
      <div className="mt-8 border border-white/10 p-3 flex flex-wrap gap-3 items-center">
        <span className="label-mono">Filter</span>
        <FilterSelect label="Priority" value={filter.priority} onChange={v=>setFilter({...filter, priority:v})} options={[["all","All"],["critical","Critical"],["high","High"],["medium","Medium"],["low","Low"]]}/>
        <FilterSelect label="Kind" value={filter.kind} onChange={v=>setFilter({...filter, kind:v})} options={[["all","All"], ...Object.entries(KIND_LABEL)]}/>
        <FilterSelect label="Dept" value={filter.department} onChange={v=>setFilter({...filter, department:v})} options={[["all","All"], ...Object.entries(DEPT_LABEL)]}/>
        <div className="flex-1"/>
        <span className="font-mono text-[10px] text-white/40 uppercase tracking-widest">{filtered.length} of {dockets.length}</span>
      </div>

      {/* Grid: Priority Radar + Kanban + Ops Log */}
      <div className="grid lg:grid-cols-[300px_1fr_320px] gap-4 mt-4">
        {/* Priority Radar */}
        <div className="panel p-4">
          <div className="label-mono text-[#ef5b6b] mb-3 flex items-center gap-2"><Radio size={12}/> Priority radar</div>
          {dash.priority_radar.length === 0 && <div className="font-mono text-xs text-white/40">All clear.</div>}
          <div className="space-y-2">
            {dash.priority_radar.map(d => (
              <button key={d.id} onClick={()=>setViewing(d.id)} className="w-full text-left border border-white/10 p-3 hover:border-[#ef5b6b]/50" data-testid={`radar-${d.id}`}>
                <div className="flex justify-between items-center gap-2">
                  <span className="tag" style={{color: PRIORITY_COLOR[d.priority]}}>{d.priority}</span>
                  <HealthBadge health={d.health}/>
                </div>
                <div className="font-display text-sm mt-2 leading-tight">{d.title}</div>
                <div className="font-mono text-[10px] text-white/50 mt-1">{d.owner_name} · {d.progress_percent}%</div>
              </button>
            ))}
          </div>
        </div>

        {/* Kanban */}
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <KanbanCol title="Not Started" color="#8C8C8C" dockets={dash.kanban.not_started} onOpen={setViewing} testid="col-not-started"/>
            <KanbanCol title="In Progress" color="#89b4fa" dockets={dash.kanban.in_progress} onOpen={setViewing} testid="col-in-progress"/>
            <KanbanCol title="At Risk / Blocked" color="#d4b876" dockets={dash.kanban.at_risk} onOpen={setViewing} testid="col-at-risk"/>
            <KanbanCol title="Completed 7d" color="#89b4fa" dockets={dash.kanban.completed} onOpen={setViewing} testid="col-completed"/>
          </div>
        </div>

        {/* Ops Log */}
        <div className="panel p-4">
          <div className="label-mono text-[#d4b876] mb-3 flex items-center gap-2">
            <span className="inline-block w-1 h-1 bg-[#d4b876] animate-pulse"/>
            Recent activity
          </div>
          {dash.recent_updates.length === 0 && <div className="font-mono text-xs text-white/40">No activity yet.</div>}
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {dash.recent_updates.map((u, i) => (
              <button key={i} onClick={()=>setViewing(u.docket_id)} className="w-full text-left border-l border-white/10 pl-3 hover:border-[#89b4fa]">
                <div className="font-mono text-[10px] text-white/40">{new Date(u.at).toLocaleString("en-CA", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" })}</div>
                <div className="font-mono text-xs text-white/80 mt-0.5">{u.by_name} · <span className="text-white/50">{u.note}</span></div>
                <div className="font-mono text-[10px] text-white/60 mt-0.5 truncate">{u.docket_title}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Financials + Upcoming + Team Load */}
      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="panel p-5">
          <div className="label-mono text-[#89b4fa] mb-3 flex items-center gap-2"><DollarSign size={12}/> Financial rollup</div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div><div className="label-mono">Allocated</div><div className="font-display text-2xl text-[#d4b876]">{currency(dash.financials.total_allocated)}</div></div>
            <div><div className="label-mono">Spent</div><div className="font-display text-2xl text-[#89b4fa]">{currency(dash.financials.total_spent)}</div></div>
          </div>
          <div className="space-y-2">
            {Object.entries(dash.financials.by_source).sort((a,b)=>b[1].allocated - a[1].allocated).map(([src, f]) => {
              const pct = f.allocated ? Math.round((f.spent / f.allocated) * 100) : 0;
              return (
                <div key={src}>
                  <div className="flex justify-between font-mono text-xs">
                    <span className="text-white/80">{src}</span>
                    <span className="text-white/60 tabular-nums">{currency(f.spent)} / {currency(f.allocated)}</span>
                  </div>
                  <div className="h-1 bg-white/5 mt-1"><div className="h-full bg-[#89b4fa]" style={{width:`${Math.min(pct,100)}%`}}/></div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="panel p-5">
          <div className="label-mono text-[#d4b876] mb-3 flex items-center gap-2"><Clock size={12}/> Upcoming deadlines · 30d</div>
          {dash.upcoming_deadlines.length === 0 ? (
            <div className="font-mono text-xs text-white/40">No deadlines in the next 30 days.</div>
          ) : (
            <div className="space-y-2">
              {dash.upcoming_deadlines.map(d => {
                const days = Math.max(0, Math.round((new Date(d.due_date) - Date.now()) / 86400000));
                return (
                  <button key={d.id} onClick={()=>setViewing(d.id)} className="w-full text-left border border-white/10 p-3 hover:border-[#d4b876]/50">
                    <div className="flex justify-between items-center gap-2">
                      <span className="font-display text-sm truncate">{d.title}</span>
                      <span className="font-mono text-[10px] text-[#d4b876] tabular-nums whitespace-nowrap">{days}D</span>
                    </div>
                    <div className="font-mono text-[10px] text-white/50 mt-1">{d.owner_name} · {d.progress_percent}%</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="panel p-5">
          <div className="label-mono text-[#89b4fa] mb-3 flex items-center gap-2"><Users size={12}/> Team load</div>
          <div className="space-y-2">
            {dash.team_load.map(t => (
              <div key={t.owner_id} className="flex justify-between items-center border border-white/10 p-2">
                <div className="min-w-0">
                  <div className="font-mono text-xs text-white/80 truncate">{t.owner_name}</div>
                  <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">{ROLE_LABEL[t.owner_role]}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {t.at_risk > 0 && <span className="font-mono text-[10px] text-[#d4b876]">⚠ {t.at_risk}</span>}
                  {t.critical > 0 && <span className="font-mono text-[10px] text-[#ef5b6b]">! {t.critical}</span>}
                  <span className="font-display text-lg text-white tabular-nums">{t.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Full docket table */}
      <div className="mt-6 panel">
        <div className="p-4 label-mono text-white/70 border-b border-white/10">All dockets ({filtered.length})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-white/50">
                <th className="px-3 py-2">Priority</th>
                <th className="px-3 py-2">Docket</th>
                <th className="px-3 py-2">Kind</th>
                <th className="px-3 py-2">Owner</th>
                <th className="px-3 py-2">Health</th>
                <th className="px-3 py-2">Progress</th>
                <th className="px-3 py-2">Due</th>
                <th className="px-3 py-2">Budget</th>
                <th className="px-3 py-2"/>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => (
                <tr key={d.id} className="border-t border-white/10 hover:bg-white/[0.03] cursor-pointer" onClick={()=>setViewing(d.id)} data-testid={`docket-row-${d.id}`}>
                  <td className="px-3 py-2"><span className="tag" style={{color: PRIORITY_COLOR[d.priority]}}>{d.priority}</span></td>
                  <td className="px-3 py-2"><div className="font-display truncate max-w-xs">{d.title}</div>{d.blockers.length > 0 && <div className="font-mono text-[10px] text-[#ef5b6b]">waiting on {d.blockers.length}</div>}</td>
                  <td className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white/60">{KIND_LABEL[d.kind]?.replace(/\s.*/, "")}</td>
                  <td className="px-3 py-2 font-mono text-xs">{d.owner_name}</td>
                  <td className="px-3 py-2"><HealthBadge health={d.health}/></td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1 bg-white/10"><div className="h-full" style={{width:`${d.progress_percent}%`, background: STATUS_COLOR[d.status]}}/></div>
                      <span className="font-mono text-[10px] tabular-nums text-white/60">{d.progress_percent}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-white/60">{d.due_date ? new Date(d.due_date).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-white/60 whitespace-nowrap">{currency(d.budget_spent)} / {currency(d.budget_allocated)}</td>
                  <td className="px-3 py-2"><ChevronRight size={14} className="text-white/30"/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <DocketEditor docket={editing === "new" ? null : editing} staff={staff} allDockets={dockets} onClose={()=>setEditing(null)} onSaved={loadAll}/>}
      {viewing && <DocketDetail id={viewing} staff={staff} allDockets={dockets} onClose={()=>setViewing(null)} onChanged={loadAll} onDelete={del} onEdit={(d)=>{ setViewing(null); setEditing(d); }}/>}
    </div>
  );
}

function VitalCard({ icon: Icon, color, label, value }) {
  return (
    <div className="panel p-4 corner-tick">
      <Icon size={16} style={{color}}/>
      <div className="font-display text-3xl mt-2" style={{color}}>{value}</div>
      <div className="label-mono mt-1">{label}</div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <label className="flex items-center gap-2 font-mono text-xs">
      <span className="text-white/50 uppercase tracking-widest text-[10px]">{label}</span>
      <select value={value} onChange={e=>onChange(e.target.value)} className="px-2 py-1 font-mono text-xs">
        {options.map(([k,l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </label>
  );
}

function HealthBadge({ health }) {
  const color = STATUS_COLOR[health] || "#8C8C8C";
  const Icon = HEALTH_ICON[health];
  return (
    <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest" style={{color}}>
      {Icon && <Icon size={10}/>}{health.replace("_"," ")}
    </span>
  );
}

function KanbanCol({ title, color, dockets, onOpen, testid }) {
  return (
    <div className="panel p-3" data-testid={testid}>
      <div className="flex justify-between items-baseline mb-3">
        <div className="label-mono" style={{color}}>{title}</div>
        <span className="font-mono text-[10px] text-white/40">{dockets.length}</span>
      </div>
      <div className="space-y-2 max-h-[560px] overflow-y-auto">
        {dockets.map(d => (
          <button key={d.id} onClick={()=>onOpen(d.id)} className="w-full text-left border border-white/10 p-3 hover:border-white/40 transition-colors" data-testid={`kanban-card-${d.id}`}>
            <div className="flex justify-between items-center">
              <span className="font-mono text-[9px] uppercase tracking-widest" style={{color: PRIORITY_COLOR[d.priority]}}>{d.priority}</span>
              <span className="font-mono text-[9px] text-white/40">{d.department}</span>
            </div>
            <div className="font-display text-sm mt-1 leading-tight">{d.title}</div>
            <div className="mt-2 h-1 bg-white/10"><div className="h-full" style={{width:`${d.progress_percent}%`, background: color}}/></div>
            <div className="flex justify-between items-center mt-2 font-mono text-[10px] text-white/50">
              <span className="truncate">{d.owner_name}</span>
              {d.blockers?.length > 0 && <span className="text-[#ef5b6b]">⛔ {d.blockers.length}</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ==================== EDITOR ====================
function DocketEditor({ docket, staff, allDockets, onClose, onSaved }) {
  const isEdit = !!docket;
  const [f, setF] = useState(docket ? {
    ...docket,
    due_date: docket.due_date ? docket.due_date.slice(0,16) : "",
    contributor_ids: docket.contributor_ids || [],
    dependency_ids: docket.dependency_ids || [],
    tags: (docket.tags || []).join(", "),
  } : {
    title:"", kind:"program_initiative", description:"", department:"GOV",
    owner_id:"", contributor_ids:[], priority:"medium", status:"not_started",
    progress_percent:0, budget_allocated:"", budget_spent:"", funding_source:"",
    due_date:"", dependency_ids:[], linked_records:{}, tags:"",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      const payload = {
        ...f,
        progress_percent: Number(f.progress_percent) || 0,
        budget_allocated: f.budget_allocated === "" ? null : Number(f.budget_allocated),
        budget_spent: f.budget_spent === "" ? null : Number(f.budget_spent),
        due_date: f.due_date ? new Date(f.due_date).toISOString() : null,
        tags: typeof f.tags === "string" ? f.tags.split(",").map(t=>t.trim()).filter(Boolean) : f.tags,
      };
      // Trim fields that don't belong on patch
      delete payload.id; delete payload.updates; delete payload.owner_name; delete payload.owner_role;
      delete payload.contributor_names; delete payload.dependencies; delete payload.blockers;
      delete payload.health; delete payload.created_by; delete payload.created_by_name;
      delete payload.created_at; delete payload.updated_at;
      if (isEdit) await api.patch(`/command/dockets/${docket.id}`, payload);
      else await api.post("/command/dockets", payload);
      toast.success(isEdit ? "Docket updated" : "Docket opened");
      onSaved(); onClose();
    } catch (e) { const msg = e.response?.data?.detail || "Failed"; setErr(msg); toast.error(msg); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 overflow-y-auto p-4" onClick={onClose}>
      <form onClick={e=>e.stopPropagation()} onSubmit={save} className="max-w-3xl mx-auto border border-white/20 bg-[#181825] p-6 space-y-4 my-8" data-testid="docket-editor">
        <div className="label-mono text-[#ef5b6b]">::: {isEdit?"EDIT":"OPEN"} DOCKET</div>
        <input required placeholder="Docket title" value={f.title} onChange={e=>setF({...f, title:e.target.value})} className="w-full px-3 py-2.5 font-mono text-sm" data-testid="docket-title"/>
        <textarea rows={2} placeholder="Description / scope" value={f.description || ""} onChange={e=>setF({...f, description:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="docket-desc"/>
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Kind" value={f.kind} onChange={v=>setF({...f, kind:v})} options={Object.entries(KIND_LABEL)} testid="docket-kind"/>
          <SelectField label="Department" value={f.department} onChange={v=>setF({...f, department:v})} options={Object.entries(DEPT_LABEL)} testid="docket-dept"/>
          <SelectField label="Priority" value={f.priority} onChange={v=>setF({...f, priority:v})} options={[["critical","Critical"],["high","High"],["medium","Medium"],["low","Low"]]} testid="docket-priority"/>
          <SelectField label="Status" value={f.status} onChange={v=>setF({...f, status:v})} options={[["not_started","Not Started"],["in_progress","In Progress"],["on_track","On Track"],["at_risk","At Risk"],["blocked","Blocked"],["completed","Completed"],["cancelled","Cancelled"]]} testid="docket-status"/>
          <SelectField label="Owner" value={f.owner_id} onChange={v=>setF({...f, owner_id:v})} options={[["","— select owner —"], ...staff.map(s=>[s.id, `${s.name} · ${ROLE_LABEL[s.role]}`])]} testid="docket-owner"/>
          <div>
            <label className="label-mono block mb-1">Due date</label>
            <input type="datetime-local" value={f.due_date} onChange={e=>setF({...f, due_date:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="docket-due"/>
          </div>
          <div>
            <label className="label-mono block mb-1">Progress ({f.progress_percent}%)</label>
            <input type="range" min="0" max="100" value={f.progress_percent} onChange={e=>setF({...f, progress_percent:e.target.value})} className="w-full" data-testid="docket-progress"/>
          </div>
          <div>
            <label className="label-mono block mb-1">Funding source</label>
            <input placeholder="ISC · CIP · Band Trust..." value={f.funding_source || ""} onChange={e=>setF({...f, funding_source:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="docket-funding"/>
          </div>
          <div>
            <label className="label-mono block mb-1">Budget allocated</label>
            <input type="number" value={f.budget_allocated} onChange={e=>setF({...f, budget_allocated:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="docket-budget-alloc"/>
          </div>
          <div>
            <label className="label-mono block mb-1">Budget spent</label>
            <input type="number" value={f.budget_spent} onChange={e=>setF({...f, budget_spent:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="docket-budget-spent"/>
          </div>
        </div>
        <div>
          <label className="label-mono block mb-1">Dependencies (blocks this docket until they complete)</label>
          <MultiSelect
            options={allDockets.filter(d => !docket || d.id !== docket.id).map(d => [d.id, `${d.title} · ${d.status}`])}
            value={f.dependency_ids}
            onChange={ids=>setF({...f, dependency_ids: ids})}
          />
        </div>
        <input placeholder="Tags (comma-separated)" value={typeof f.tags === "string" ? f.tags : (f.tags || []).join(", ")} onChange={e=>setF({...f, tags:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="docket-tags"/>
        {err && <div className="text-[#ef5b6b] font-mono text-xs">{err}</div>}
        <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
          <button type="button" onClick={onClose} className="btn-cta">Cancel</button>
          <button type="submit" disabled={saving} className="btn-cta primary" data-testid="save-docket-btn">{saving?"Saving...":isEdit?"Update":"Open Docket"}</button>
        </div>
      </form>
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="label-mono block mb-1">{label}</label>
      <select value={value} onChange={e=>onChange(e.target.value)} className="w-full px-3 py-2 font-mono text-sm" data-testid={testid}>
        {options.map(([k,l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </div>
  );
}

function MultiSelect({ options, value, onChange }) {
  return (
    <div className="border border-white/15 max-h-40 overflow-y-auto p-2 space-y-1">
      {options.length === 0 && <div className="font-mono text-[10px] text-white/40">No other dockets</div>}
      {options.map(([id, label]) => {
        const on = value.includes(id);
        return (
          <label key={id} className="flex items-center gap-2 font-mono text-xs cursor-pointer hover:bg-white/5 p-1">
            <input type="checkbox" checked={on} onChange={()=>onChange(on ? value.filter(v=>v!==id) : [...value, id])}/>
            <span className="text-white/80 truncate">{label}</span>
          </label>
        );
      })}
    </div>
  );
}

// ==================== DETAIL ====================
function DocketDetail({ id, staff, allDockets, onClose, onChanged, onDelete, onEdit }) {
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const [noteStatus, setNoteStatus] = useState("");
  const [noteProgress, setNoteProgress] = useState("");
  const load = () => api.get(`/command/dockets/${id}`).then(r=>setD(r.data)).catch(()=>{});
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;

  const submitNote = async (e) => {
    e.preventDefault();
    const payload = { note };
    if (noteStatus) payload.status = noteStatus;
    if (noteProgress !== "") payload.progress_percent = Number(noteProgress);
    try {
      await api.post(`/command/dockets/${id}/update`, payload);
      toast.success("Update logged");
      setNote(""); setNoteStatus(""); setNoteProgress("");
      load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 overflow-y-auto p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="max-w-4xl mx-auto border border-white/20 bg-[#181825] p-8 my-8" data-testid="docket-detail">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="tag" style={{color: PRIORITY_COLOR[d.priority]}}>{d.priority}</span>
              <span className="tag" style={{color: STATUS_COLOR[d.status]}}>{d.status.replace("_"," ")}</span>
              <HealthBadge health={d.health}/>
              <span className="font-mono text-[10px] text-white/50 uppercase tracking-widest">{KIND_LABEL[d.kind]} · {DEPT_LABEL[d.department]}</span>
            </div>
            <h2 className="font-display text-3xl mt-3">{d.title}</h2>
            {d.description && <p className="text-white/70 mt-3">{d.description}</p>}
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={()=>onEdit(d)} className="btn-cta text-xs" data-testid="detail-edit"><Edit3 size={12}/> Edit</button>
            <button onClick={()=>onDelete(d.id)} className="btn-cta danger text-xs" data-testid="detail-delete"><Trash2 size={12}/></button>
            <button onClick={onClose} className="btn-cta text-xs" data-testid="detail-close">Close</button>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-3 mt-6 font-mono text-xs">
          <div><div className="label-mono">Owner</div><div className="mt-1">{d.owner_name}<div className="text-[10px] text-white/50">{ROLE_LABEL[d.owner_role]}</div></div></div>
          <div><div className="label-mono">Due</div><div className="mt-1">{d.due_date ? new Date(d.due_date).toLocaleString() : "—"}</div></div>
          <div><div className="label-mono">Progress</div><div className="mt-1">{d.progress_percent}%<div className="h-1 bg-white/10 mt-1"><div className="h-full bg-[#89b4fa]" style={{width:`${d.progress_percent}%`}}/></div></div></div>
          <div><div className="label-mono">Budget</div><div className="mt-1">{currency(d.budget_spent)} / {currency(d.budget_allocated)}<div className="text-[10px] text-white/50">{d.funding_source || "—"}</div></div></div>
        </div>

        {d.dependencies?.length > 0 && (
          <div className="mt-6 border border-white/10 p-4">
            <div className="label-mono text-[#ef5b6b] mb-2 flex items-center gap-2"><GitBranch size={12}/> DEPENDENCIES</div>
            <div className="space-y-1">
              {d.dependencies.map(dep => (
                <div key={dep.id} className="font-mono text-xs flex items-center justify-between">
                  <span className={dep.status === "completed" ? "text-white/50 line-through" : "text-white"}>{dep.title}</span>
                  <span className="tag" style={{color: STATUS_COLOR[dep.status]}}>{dep.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Post update */}
        <form onSubmit={submitNote} className="mt-6 border border-white/10 p-4 space-y-3">
          <div className="label-mono text-[#89b4fa] flex items-center gap-2"><MessageSquare size={12}/> POST UPDATE</div>
          <textarea rows={2} required placeholder="What changed? Progress, blockers, notes..." value={note} onChange={e=>setNote(e.target.value)} className="w-full px-3 py-2 font-mono text-sm" data-testid="update-note"/>
          <div className="grid grid-cols-2 gap-3">
            <select value={noteStatus} onChange={e=>setNoteStatus(e.target.value)} className="px-3 py-2 font-mono text-xs" data-testid="update-status">
              <option value="">Keep status ({d.status})</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="on_track">On Track</option>
              <option value="at_risk">At Risk</option>
              <option value="blocked">Blocked</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <input type="number" min="0" max="100" placeholder={`Progress (current ${d.progress_percent}%)`} value={noteProgress} onChange={e=>setNoteProgress(e.target.value)} className="px-3 py-2 font-mono text-xs" data-testid="update-progress"/>
          </div>
          <button type="submit" className="btn-cta primary text-xs" data-testid="update-submit">Log update →</button>
        </form>

        {/* Timeline */}
        <div className="mt-6">
          <div className="label-mono text-white/70 mb-3">TIMELINE ({d.updates?.length || 0})</div>
          <div className="space-y-3">
            {(d.updates || []).slice().reverse().map(u => (
              <div key={u.id} className="border-l-2 border-white/10 pl-4">
                <div className="font-mono text-[10px] text-white/40">{new Date(u.at).toLocaleString()}</div>
                <div className="font-mono text-xs text-white/90 mt-0.5">{u.by_name} · <span className="text-white/60">{ROLE_LABEL[u.by_role]}</span></div>
                <div className="text-white/80 text-sm mt-1">{u.note}</div>
                <div className="flex gap-3 mt-1 font-mono text-[10px]">
                  {u.status_change && <span className="text-[#d4b876]">{u.status_change.from} → {u.status_change.to}</span>}
                  {u.progress_change && <span className="text-[#89b4fa]">{u.progress_change.from}% → {u.progress_change.to}%</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
